import React, { useEffect, useMemo } from 'react';
import { DevBanner, Typography } from 'app/components/ui';
import { useState } from 'react';
import { Modal as BootstrapModal } from 'react-bootstrap';
import { i18n } from 'app/utils/i18n';
import { useCalculations, useExternalReferences } from 'app/api/tag-queries/taxonomy';
import { ExternalReferenceResponse, ExternalReferenceItem, TaxonomyConceptType, UUID } from 'app/api/tag-queries/types';
import { useEditorSelector } from 'app/state/contexts/EditorContext';
import { useDocumentTaxonomyConfig, useExtensions } from 'app/api/tag-queries';
import { CalculationsSection } from '../tagInfo/CalculationsSection';

import './Modal.scss';

export interface DetailsTagConcept {
  id?: UUID;
  name: string;
  conceptType?: TaxonomyConceptType;
  label?: string | null;
  notes?: string;
}

interface DetailsModalProps {
  show: boolean;
  onClose: () => void;
  tagConcept: DetailsTagConcept | null;
}

export const DetailsModal = ({ show, onClose, tagConcept }: DetailsModalProps) => {
  const [activeTab, setActiveTab] = useState('properties');
  const { pDocument } = useEditorSelector((editor: any) => editor);

  const {
    data: calculationGroups,
    isLoading: calculationsLoading,
    isFetching: calculationsFetching,
  } = useCalculations(show ? pDocument?.superId : undefined, show ? tagConcept?.name : null);
  const { data: externalReferences, isLoading: externalReferencesLoading } = useExternalReferences(
    show ? pDocument?.superId : undefined,
    show ? tagConcept?.name : null
  );

  const { data: taxonomyConfig } = useDocumentTaxonomyConfig(show ? pDocument?.superId : undefined);
  const activeTaxonomyIdForAnchoring =
    taxonomyConfig?.taxonomySelections?.find((t) => t.isActive)?.taxonomyId ?? taxonomyConfig?.taxonomySelections?.[0]?.taxonomyId;
  const { data: extensions } = useExtensions(show ? pDocument?.superId : undefined, show ? activeTaxonomyIdForAnchoring : undefined);
  const matchingExtension = useMemo(() => (extensions ?? []).find((e) => e.name === tagConcept?.name), [extensions, tagConcept?.name]);

  const widerAnchorNameForLinks = matchingExtension?.widerAnchor?.name ?? null;
  const { data: widerAnchorReferences, isLoading: widerAnchorReferencesLoading } = useExternalReferences(
    show ? pDocument?.superId : undefined,
    show && matchingExtension ? widerAnchorNameForLinks : null
  );
  const effectiveExternalReferences = matchingExtension ? widerAnchorReferences : externalReferences;
  const effectiveReferencesLoading = matchingExtension ? widerAnchorReferencesLoading : externalReferencesLoading;

  const detailsTabs = [
    { key: 'properties', label: i18n('tag.properties.title') },
    { key: 'calculations', label: i18n('tag.calculations.title') },
    { key: 'notes', label: i18n('tag.notes.title') },
    { key: 'anchoring', label: i18n('tag.anchoring.title') },
    { key: 'links', label: i18n('tag.links.title') || 'Länkar' },
  ];

  useEffect(() => {
    if (!show) {
      setActiveTab('properties');
    }
  }, [show]);

  // Tag-specific tabs are only enabled when viewing an actual tag from the document
  const isTagView = !!tagConcept?.id;

  const label = tagConcept?.label ?? 'No label';

  const propertiesToShow = tagConcept
    ? [
        { label: i18n('tag.name.title'), value: tagConcept.name.split(':')[1] },
        { label: i18n('tag.namespace.title'), value: tagConcept.name.split(':')[0] },
        { label: i18n('tag.details.data.type'), value: tagConcept?.conceptType?.baseXbrliType ?? '-' },
        { label: i18n('tag.period.type'), value: tagConcept?.conceptType?.periodType ?? '-' },
        { label: i18n('tag.details.abstract'), value: tagConcept?.conceptType?.isAbstract ? 'true' : 'false' },
        { label: i18n('tag.details.balance'), value: tagConcept?.conceptType?.balance ?? '-' },
        { label: i18n('tag.details.nillable'), value: '-' },
      ]
    : [];

  return (
    <BootstrapModal show={show} onHide={onClose} size="lg" scrollable contentClassName="tags-modal__content">
      <BootstrapModal.Header className="tags-modal__header">
        <button
          onClick={(e) => {
            e.persist();
            onClose();
          }}
          type="button"
          data-bs-dismiss="modal"
          aria-label="Close"
          className="tags-modal__close-btn"
        >
          <i className="fa-solid fa-xmark" />
        </button>

        <Typography variant="body-7" fontWeight="semibold">
          {label}
        </Typography>

        <Typography variant="body-5" fontWeight="light">
          Vad vill vi visa som subtitle här?
        </Typography>
      </BootstrapModal.Header>

      <BootstrapModal.Body className="tags-modal__body">
        <div className="tags-modal__tab-bar">
          {detailsTabs.map((tab) => (
            <button
              key={tab.key}
              className={`tags-modal__tab ${activeTab === tab.key ? 'tags-modal__tab--active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
              disabled={!isTagView && tab.key !== 'properties' && tab.key !== 'links'}
            >
              <Typography variant="body-5" fontWeight="semibold">
                {tab.label}
              </Typography>
            </button>
          ))}
        </div>

        <div>
          {activeTab === 'properties' && (
            <div className="tags-modal__properties">
              {propertiesToShow.map((prop, idx) => (
                <React.Fragment key={idx}>
                  <Typography variant="body-5" fontWeight="semibold">
                    {prop.label}
                  </Typography>
                  <Typography variant="body-5" fontWeight="light" style={{ wordBreak: 'break-all' }}>
                    {prop.value}
                  </Typography>
                </React.Fragment>
              ))}
            </div>
          )}

          {activeTab === 'calculations' && (
            <CalculationsSection calculationGroups={calculationGroups} isLoading={calculationsLoading} isFetching={calculationsFetching} />
          )}

          {activeTab === 'notes' && (
            <div className="d-flex flex-column gap-3">
              {matchingExtension?.description && (
                <div>
                  <Typography variant="body-3" fontWeight="semibold" color="#717077">
                    {i18n('tag.extension.create.description')}
                  </Typography>
                  <Typography variant="body-5" style={{ wordBreak: 'break-all' }}>
                    {matchingExtension.description}
                  </Typography>
                </div>
              )}
              <div>
                {matchingExtension && (
                  <Typography variant="body-3" fontWeight="semibold" color="#717077">
                    {i18n('tag.notes.title')}
                  </Typography>
                )}
                <Typography variant="body-5" fontWeight="light" style={{ wordBreak: 'break-all' }}>
                  {tagConcept?.notes ?? i18n('tag.notes.none')}
                </Typography>
              </div>
            </div>
          )}

          {activeTab === 'anchoring' &&
            (matchingExtension ? (
              <div className="d-flex flex-column gap-3">
                <div>
                  <Typography variant="body-5" fontWeight="semibold">
                    {i18n('tag.extension.create.wider.anchor')}
                  </Typography>
                  <Typography variant="body-5">{matchingExtension.widerAnchor.name}</Typography>
                </div>
                {matchingExtension.narrowerAnchors.length > 0 && (
                  <div>
                    <Typography variant="body-5" fontWeight="semibold">
                      {i18n('tag.extension.create.narrower.anchor')}
                    </Typography>
                    {matchingExtension.narrowerAnchors.map((a) => (
                      <Typography key={a.conceptId} variant="body-5">
                        {a.name}
                      </Typography>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div>
                <DevBanner size="xl" description="Denna funtionalitet har ännu inte implementerats." />
                <Typography variant="body-5">{i18n('tag.anchoring.none')}</Typography>
              </div>
            ))}

          {activeTab === 'links' && (
            <div className="d-flex flex-column gap-3">
              {matchingExtension && (
                <Typography variant="body-3" fontWeight="semibold" color="#717077">
                  {i18n('tag.extension.create.wider.anchor')}: {matchingExtension.widerAnchor.name}
                </Typography>
              )}
              <LinksSection externalReferences={effectiveExternalReferences} isLoading={effectiveReferencesLoading} />
            </div>
          )}
        </div>
      </BootstrapModal.Body>
    </BootstrapModal>
  );
};

interface LinksSectionProps {
  externalReferences: ExternalReferenceResponse[] | null | undefined;
  isLoading?: boolean;
}

const LinksSection = ({ externalReferences, isLoading }: LinksSectionProps) => {
  if (isLoading) return null;

  const allReferences: ExternalReferenceItem[] = [];
  if (externalReferences) {
    for (const ref of externalReferences) {
      if (Array.isArray(ref.iasIfrsSicIfricReferences)) {
        allReferences.push(...ref.iasIfrsSicIfricReferences);
      }
      if (Array.isArray(ref.extraReferences)) {
        allReferences.push(...ref.extraReferences);
      }
    }
  }

  if (allReferences.length === 0) {
    return <Typography variant="body-5">{i18n('tag.links.none') || 'Inga länkar'}</Typography>;
  }

  return (
    <div className="flex flex-col gap-3">
      {allReferences.map((ref, idx) => {
        const { parts } = ref;
        const label = [parts.Name, parts.Number].filter(Boolean).join(' ');
        const detail = [parts.Paragraph && `\u00A7${parts.Paragraph}`, parts.Subparagraph && `(${parts.Subparagraph})`].filter(Boolean).join(' ');

        return (
          <div key={idx} className="flex flex-col gap-1">
            <Typography variant="body-5" fontWeight="semibold">
              {label} {detail}
            </Typography>
            {parts.URI && (
              <a
                href={parts.URI}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: '0.75rem',
                  wordBreak: 'break-all',
                  color: '#1a73e8',
                  textDecoration: 'underline',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                {parts.URI} <i className="fa-solid fa-arrow-up-right-from-square" style={{ fontSize: '0.65rem' }} />
              </a>
            )}
          </div>
        );
      })}
    </div>
  );
};
